import React, { useState, useEffect } from 'react';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'framer-motion';
import styles from './NewLanding.module.css';
import pricingStyles from '../NewPricing/NewPricing.module.css';
import Navbar from './Navbar'; // Reverted to original Navbar import
import Works from './Works';
import PromoNotification from '../PromoNotification/PromoNotification';
import posthog from '../../utils/posthog';
import DecryptedText from './DecryptedText';
import dynamic from 'next/dynamic';
import Footer from '../Footer/Footer';
import { InfiniteMovingCards } from '../ui/infinite-moving-cards';
import { CurvedCard } from '../ui/CurvedCard';
import ChatSearchDemo from './ChatSearchDemo';

// Dynamic import for AuroraGrain to prevent SSR issues
const AuroraGrain = dynamic(() => import('./AuroraGrain'), {
  ssr: false,
  loading: () => <div className={styles.auroraBackground} style={{ minHeight: '100vh' }} />
});

// Dynamic import for LightBeams (cathedral god rays effect)
const LightBeams = dynamic(() => import('./LightBeams'), {
  ssr: false,
  loading: () => <div className={styles.auroraBackground} style={{ minHeight: '100vh' }} />
});



const testimonials = [
  {
    quote: "Zeus completely changed our audio workflow. Instead of spending hours searching for the right sounds, I just describe what I need and it instantly knows whether to search, generate, or enhance. It's like having an audio expert on the team.",
    name: "Mark Johnson",
    title: "Indie Game Developer",
  },
  {
    quote: "The intelligent routing is incredible. I can upload rough audio and Zeus automatically optimizes it for Unity, or ask for epic battle music and get professional results. One interface for everything audio.",
    name: "Sarah Chen",
    title: "Sound Designer",
  },
  {
    quote: "Finally, an AI that understands creative context. Zeus analyzes our project needs and delivers exactly the right audio workflow. No more technical guessing—just creative results.",
    name: "Alex Torres",
    title: "Creative Director",
  },
  {
    quote: "Zeus has revolutionized our game audio pipeline. The AI instantly understands whether we need ambient sounds, combat effects, or character voices. It's cut our audio production time by 70%.",
    name: "Emily Rodriguez",
    title: "Audio Lead, Pixel Studios",
  },
  {
    quote: "The mastering quality is phenomenal. Zeus automatically optimizes our tracks for different platforms—Unity, Spotify, broadcast—with perfect loudness standards. It's like having a mastering engineer available 24/7.",
    name: "David Kim",
    title: "Music Producer",
  },
  {
    quote: "What impressed me most is Zeus's contextual intelligence. It doesn't just generate audio—it understands mood, genre, and project requirements. Our film scoring workflow has never been more efficient.",
    name: "Maria Gonzalez",
    title: "Film Composer",
  },
  {
    quote: "The AI's ability to understand our creative vision is mind-blowing. I can describe a scene and Zeus generates the perfect audio that matches the mood and atmosphere we're going for.",
    name: "James Wilson",
    title: "Game Director",
  },
  {
    quote: "Zeus has transformed how we approach sound design. Instead of spending days on audio, we can iterate quickly and focus on what matters most - creating amazing player experiences.",
    name: "Lisa Park",
    title: "Audio Producer",
  },
  {
    quote: "The platform's intelligence is incredible. It knows exactly when to use existing sounds versus generating new ones, saving us countless hours of manual work.",
    name: "Carlos Mendez",
    title: "Senior Sound Designer",
  },
  {
    quote: "Zeus understands the technical requirements of different platforms. Our audio automatically gets optimized for mobile, console, and PC without any manual adjustments.",
    name: "Rachel Green",
    title: "Technical Audio Lead",
  },
  {
    quote: "The contextual awareness is game-changing. Zeus doesn't just create sounds - it creates the right sounds for the right moment in our games.",
    name: "Michael Chang",
    title: "Creative Audio Director",
  },
  {
    quote: "We've cut our audio production time in half while improving quality. Zeus is like having a team of expert sound designers available 24/7.",
    name: "Amanda Foster",
    title: "Studio Audio Manager",
  }
];

// Lightweight overlay that shows a vertical compare line and controls audio volume based on hover position
const CompareLineOverlay = () => {
  const [percent, setPercent] = useState(50);
  const containerRef = React.useRef(null);
  const audioRef = React.useRef(null);

  // Lazily create audio on mount (client-side only)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const audio = new Audio('/landing/merged-audio%20(18).wav');
    audio.loop = true;
    audio.volume = 0;
    audioRef.current = audio;
    return () => {
      try { audio.pause(); } catch { }
      audioRef.current = null;
    };
  }, []);

  // Keep audio in sync with the sibling video (restart at 0 when video restarts/plays)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const parent = el.parentElement;
    if (!parent) return;
    const video = parent.querySelector('video');
    if (!video) return;

    const resetAudioToStart = () => {
      if (!audioRef.current) return;
      try {
        audioRef.current.currentTime = 0;
        audioRef.current.volume = 0; // start silent just like the video
        // Keep audio transport running for precise sync after user interaction
        audioRef.current.play().catch(() => { });
      } catch { }
    };

    const onPlay = () => resetAudioToStart();
    const onEnded = () => resetAudioToStart();
    const onLoadedMetadata = () => resetAudioToStart();
    const onPause = () => { try { audioRef.current && audioRef.current.pause(); } catch { } };
    const onTimeUpdate = () => {
      if (!audioRef.current) return;
      try {
        // Mirror video's transport position on each tick
        const vt = video.currentTime || 0;
        const at = audioRef.current.currentTime || 0;
        if (Math.abs(vt - at) > 0.05) {
          audioRef.current.currentTime = vt;
        }
        // If video is playing, ensure audio transport is playing (muted until user moves line)
        if (!video.paused && audioRef.current.paused) {
          audioRef.current.play().catch(() => { });
        }
      } catch { }
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('ended', onEnded);
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('pause', onPause);
    video.addEventListener('timeupdate', onTimeUpdate);
    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('timeupdate', onTimeUpdate);
    };
  }, []);

  const updateFromClientX = (clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    const p = Math.round((x / rect.width) * 100);
    setPercent(p);
    if (audioRef.current) {
      audioRef.current.volume = Math.min(Math.max(p / 100, 0), 1);
      if (p > 2 && audioRef.current.paused) {
        audioRef.current.play().catch(() => { });
      }
      if (p <= 2) audioRef.current.volume = 0;
    }
  };

  return (
    <div
      ref={containerRef}
      style={{ position: 'absolute', inset: 0, cursor: 'ew-resize' }}
      onMouseEnter={(e) => updateFromClientX(e.clientX)}
      onMouseMove={(e) => updateFromClientX(e.clientX)}
      onMouseLeave={() => { if (audioRef.current) audioRef.current.volume = 0; }}
      onTouchStart={(e) => { if (e.touches[0]) updateFromClientX(e.touches[0].clientX); }}
      onTouchMove={(e) => { if (e.touches[0]) updateFromClientX(e.touches[0].clientX); }}
    >
      {/* Glow trail (fades at ends, not full height) */}
      <div
        style={{
          position: 'absolute',
          top: '10%',
          bottom: '10%',
          left: `calc(${percent}% - 2px)`,
          width: 4,
          background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.25) 10%, rgba(255,255,255,0.25) 90%, rgba(255,255,255,0) 100%)',
          filter: 'blur(5px)',
          borderRadius: 2,
        }}
      />
      {/* Main animated line with small handle (thinner, not full height) */}
      <div
        className="compare-line"
        style={{
          position: 'absolute',
          top: '10%',
          bottom: '10%',
          left: `calc(${percent}% - 0.5px)`,
          width: 1,
          background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.95) 15%, rgba(255,255,255,0.95) 85%, rgba(255,255,255,0) 100%)',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 10,
            height: 10,
            borderRadius: '9999px',
            background: 'rgba(255,255,255,0.98)',
            boxShadow: '0 0 10px rgba(255,255,255,0.9), 0 0 20px rgba(255,255,255,0.5)'
          }}
        />
      </div>
    </div>
  );
};

const NewLanding = () => {
  const [promoVisible, setPromoVisible] = useState(true);
  // FAQ state (reused from pricing for consistent UX)
  const [activeQuestion, setActiveQuestion] = useState(null);

  const handlePromoDismiss = () => {
    setPromoVisible(false);
  };

  // FAQ helpers and content (copied from NewPricing)
  const toggleQuestion = (index) => {
    setActiveQuestion(activeQuestion === index ? null : index);
  };

  const faqQuestions = [
    {
      question: "Isn't AI audio too generic for professional projects?",
      answer: "Not anymore. Most sound teams say their biggest bottleneck isn't creativity — it's repetitive technical work. Zenyai automates syncing and rough sound passes, while leaving room for your team's creative polish. It's not replacing sound designers, it's giving them hours back."
    },
    {
      question: "Will this actually save money for studios?",
      answer: "Yes. Audio post is often 20–30% of a project's budget, and most of that is manual editing. By auto-syncing and generating draft audio, Zenyai shrinks production cycles dramatically, so studios can reallocate budget toward storytelling instead of grunt work."
    },
    {
      question: "How does this work with existing DAWs and workflows?",
      answer: "Zenyai exports directly into Pro Tools, Logic, Reaper, Ableton, Unity, and Unreal. You can bring in your own libraries, use AI-generated sounds, or both. That means your current workflow stays intact — just faster and easier."
    },
    {
      question: "Is AI audio secure for client projects?",
      answer: "Yes. Security and IP ownership are the #1 barriers studios raise about AI. Zenyai uses SOC 2 compliance, GDPR standards, and ephemeral processing — meaning files are never stored or used to train models. Your client's assets stay private."
    },
    {
      question: "What if I don't have a dedicated sound designer?",
      answer: "Many indie teams don't. Zenyai was built so non-audio pros can still create professional results. Describe what you want — footsteps, ambient music, dialogue polish — and the AI handles the technical side, while giving you tools to refine the creative."
    },
    {
      question: "Can this replace stock sound libraries?",
      answer: "Yes and no. Most creators complain that stock libraries are either too limited or inconsistent. Zenyai integrates your existing sounds, lets you search intelligently, and fills in the gaps with AI-generated audio. The result: no more hours wasted hunting for \"close enough.\""
    },
    {
      question: "Does this work for ads, games, and streaming — not just film?",
      answer: "Absolutely. Audio specs vary widely — broadcast, YouTube, Unity, Unreal, etc. Zenyai automatically optimizes output for the platform you choose. Whether it's film, streaming, games, or ads, you get audio that's synced and delivered to standard."
    }
  ];

  // Track landing page view for A/B testing
  useEffect(() => {
    // Identify this as the "new-landing" variant for A/B testing
    posthog.capture('landing_page_viewed', {
      variant: 'new-landing',
      focus: 'general',
      page_type: 'landing'
    });
  }, []);

  // Track CTA clicks
  const trackCTAClick = (ctaType, ctaLocation) => {
    posthog.capture('landing_cta_clicked', {
      variant: 'new-landing',
      cta_type: ctaType,
      cta_location: ctaLocation
    });
  };

  return (
    <div className={styles.container}>
      <Head>
        <title>Zenyai | AI agent for audio pre-production</title>
        <meta name="description" content="Zenyai is the pre-production workspace where sound designers and devs co-create audio" />
        <link rel="icon" href="/favicon.ico" />
      </Head>

      <main className={styles.main}>
        <PromoNotificationWithCallback
          visible={promoVisible}
          onDismiss={handlePromoDismiss}
        />

        {/* Black Friday Announcement Bar temporarily hidden */}
        {/*
        <div className={styles.announcementBar}>
          <div className={styles.announcementPill}>
            <div className={styles.announcementContent}>
              <span className={styles.announcementText}>Black Friday Deal for the Month!</span>
              <Link
                href="/new-pricing"
                className={styles.announcementArrow}
                onClick={() => trackCTAClick('black_friday', 'announcement_bar')}
                aria-label="Go to pricing"
              >
                Get limited seat →
              </Link>
            </div>
          </div>
        </div>
        */}

        <Navbar promoVisible={promoVisible} /> {/* Reverted to original Navbar usage*/}

        <section className={styles.hero}>
          <CurvedCard
            fullRounded
            auroraComponent={
              <AuroraGrain
                colorStops={["#00A0B0", "#9C0076", "#FF4500"]}
                blend={0.7}
                amplitude={1.2}
                speed={0.3}
                noiseIntensity={0.05}
                noiseScale={200.0}
              />
            }
          >
            <div className={styles.heroContent}>
              {/* New Feature Announcement Bar */}
              {/* <div className={styles.featureAnnouncement}>
            6-Day Free Trial — Limited Time Only
            </div> */}

              <h1 className={styles.heroTitle}>
                <span
                  className={styles.weAre}
                  style={{
                    fontSize: '1em',
                    fontWeight: '300',
                    color: '#ffffff',
                    textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  From 5 Hours to 30 Minutes<br />
                </span>
              </h1>
              <div className={styles.heroDescription}>
                <p>Your Complete Audio Workflow in One Agent. It searches 20M+ sounds, generates audio, auto-syncs video, manages assets. No library hopping, no app switching.</p>
              </div>
              <div className={styles.buttonContainer}>
                <Link
                  href="/dashboard"
                  className={`${styles.button} ${styles.primaryButton}`}
                  onClick={() => trackCTAClick('start_free', 'hero')}
                  style={{ fontWeight: 700 }}
                >
                  Build Your Agent
                </Link>
                {/* <Link 
                href="/contact" 
                className={`${styles.button} ${styles.secondaryButton}`}
                onClick={() => trackCTAClick('book_demo', 'hero')}
                style={{ fontWeight: 700 }}
              >
                Book a Demo
              </Link> */}
              </div>
              <p style={{
                marginTop: '0.5rem',
                fontSize: '0.9375rem',
                color: 'rgba(255, 255, 255, 0.6)',
                textAlign: 'center',
                width: '100%'
              }}>
                {/* Search Freesound, Pond5, your library and more */}
              </p>

              {/* Platform Video Showcase (above Trusted logos) */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '0 2rem',
                width: '100%',
                marginTop: '1.5rem'
              }}>
                <div style={{
                  position: 'relative',
                  width: '85%',
                  maxWidth: '1400px'
                }}>
                  {/* Top glow effect */}
                  <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px', background: 'linear-gradient(90deg, transparent 0%, rgba(0, 255, 255, 0.6) 20%, rgba(0, 200, 255, 0.8) 50%, rgba(0, 255, 255, 0.6) 80%, transparent 100%)', filter: 'blur(8px)', zIndex: 1, borderRadius: '16px 16px 0 0' }} />
                  <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    disablePictureInPicture
                    controlsList="nodownload nofullscreen noremoteplayback"
                    style={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: '16px',
                      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
                      display: 'block',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      position: 'relative',
                      willChange: 'transform',
                      backfaceVisibility: 'hidden',
                      transform: 'translateZ(0)'
                    }}
                    onLoadedMetadata={(e) => { e.target.play().catch(() => { }); }}
                  >
                    <source src="/landing/zenyai-platform-demo-compressed.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>

              <div className={styles.projectCollabSection}>
                <div className={styles.projectCollabContainer}>
                  <h3 className={styles.projectCollabTitle}>Trusted by the Best</h3>
                  {/* <p className={styles.projectCollabSubtitle}>Join these projects already using Zenyai</p> */}

                  <div className={styles.trustedLogos}>
                    {/* Left branch */}
                    <Image
                      src="/landing/branch-icon-28.jpg"
                      alt="Decorative branch"
                      className={styles.branchIcon}
                      width={80}
                      height={80}
                      style={{
                        height: 'auto',
                        opacity: 0.7,
                        filter: 'brightness(0.8) brightness(0) invert(1)',
                        transform: 'scaleX(-1)'
                      }}
                    />

                    <Image
                      src="/landing/Oscar-Logo.png"
                      alt="Oscar Logo"
                      className={`${styles.trustedLogo} ${styles.oscarLogo}`}
                      width={200}
                      height={80}
                    />
                    <Image
                      src="/landing/Activision_logo.webp"
                      alt="Activision Logo"
                      className={styles.trustedLogo}
                      width={200}
                      height={80}
                    />
                    <Image
                      src="/landing/grammy_awards_logo.webp"
                      alt="Grammy Awards Logo"
                      className={styles.trustedLogo}
                      width={200}
                      height={80}
                    />

                    {/* Right branch */}
                    <Image
                      src="/landing/branch-icon-28.jpg"
                      alt="Decorative branch"
                      className={styles.branchIcon}
                      width={80}
                      height={80}
                      style={{
                        height: 'auto',
                        opacity: 0.7,
                        filter: 'brightness(0.8) brightness(0) invert(1)'
                      }}
                    />
                  </div>

                  {/* <div className={styles.projectCollabGrid}>
                  <div className={styles.projectCollabItem}>
                    <img 
                      src="/images/Project-collab/kwixXq3dMW8gZhCUECafNx9h8.avif" 
                      alt="Project collaboration showcase" 
                      className={styles.projectCollabImage}
                    />
                    <div className={styles.projectCollabOverlay}>
                      <span className={styles.projectCollabName}>HAVENDOCK</span>
                    </div>
                  </div>
                  <div className={styles.projectCollabItem}>
                    <img 
                      src="/images/Project-collab/7hlRZyDMxV46O4og6eOWowmZkBw.avif" 
                      alt="Project collaboration showcase" 
                      className={styles.projectCollabImage}
                    />
                    <div className={styles.projectCollabOverlay}>
                      <span className={styles.projectCollabName}>UNDEAD RESILIENCE</span>
                    </div>
                  </div>
                  <div className={styles.projectCollabItem}>
                    <img 
                      src="/images/Project-collab/etCFmEugcRfaHbllpu3vCWRWMc.avif" 
                      alt="Project collaboration showcase" 
                      className={styles.projectCollabImage}
                    />
                    <div className={styles.projectCollabOverlay}>
                      <span className={styles.projectCollabName}>HACKRUN</span>
                    </div>
                  </div>
                  <div className={styles.projectCollabItem}>
                    <img 
                      src="/images/Project-collab/euTxHouIq18m8loWzAcZOY81WTY.avif" 
                      alt="Project collaboration showcase" 
                      className={styles.projectCollabImage}
                    />
                    <div className={styles.projectCollabOverlay}>
                      <span className={styles.projectCollabName}>FATE OF THE CROWN</span>
                    </div>
                  </div>
                  <div className={styles.projectCollabItem}>
                    <img 
                      src="/images/Project-collab/q7IJs2zunbFmT76LfzEjL1Q.avif" 
                      alt="Project collaboration showcase" 
                      className={styles.projectCollabImage}
                    />
                    <div className={styles.projectCollabOverlay}>
                      <span className={styles.projectCollabName}>REALMRUSH</span>
                    </div>
                  </div>
                </div> */}
                </div>
              </div>
            </div>
          </CurvedCard>
        </section>



        {/* Video-to-Sound Effects Section */}
        <section className={styles.videoToSoundSection}>
          <div className={styles.videoToSoundContainer}>
            <div className={styles.videoToSoundCard} style={{ minHeight: '640px' }}>
              <div className={styles.videoToSoundMockup} style={{ minHeight: '640px' }}>
                <div className={styles.videoToSoundInterface}>
                  <div className={styles.videoToSoundHeader}>
                    <div className={styles.videoToSoundTitle}>Video Analysis & Auto-Sound Generation</div>
                    <div className={styles.videoToSoundControls}>
                      <span className={styles.videoToSoundDot}></span>
                      <span className={styles.videoToSoundDot}></span>
                      <span className={styles.videoToSoundDot}></span>
                    </div>
                  </div>
                  <div className={styles.videoToSoundContent}>
                    <div className={styles.videoUploadArea}>
                      <div className={styles.videoPreview} style={{ height: '260px' }}>
                        <video
                          autoPlay
                          muted
                          loop
                          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }}
                        >
                          <source src="/landing/Untitled video - Made with Clipchamp (3).mp4" type="video/mp4" />
                        </video>
                      </div>
                      <div className={styles.analysisProgress}>
                        <div className={styles.progressBar}>
                          <div className={styles.progressFill}></div>
                        </div>
                        <span className={styles.progressText}>Analyzing video content...</span>
                      </div>
                    </div>
                    <div className={styles.generatedSounds}>
                      <div className={styles.soundItem}>
                        <div className={styles.soundWaveform}></div>
                        <span>Footsteps on Grass</span>
                        <div className={styles.soundTime}>0:00</div>
                      </div>
                      <div className={styles.soundItem}>
                        <div className={styles.soundWaveform}></div>
                        <span>Wind Through Trees</span>
                        <div className={styles.soundTime}>0:02</div>
                      </div>
                      <div className={styles.soundItem}>
                        <div className={styles.soundWaveform}></div>
                        <span>Bird Chirping</span>
                        <div className={styles.soundTime}>0:04</div>
                      </div>
                      <div className={styles.soundItem}>
                        <div className={styles.soundWaveform}></div>
                        <span>Distant Thunder</span>
                        <div className={styles.soundTime}>0:06</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.videoToSoundContent}>
                <h2 className={styles.creativeFlowTitle}>Your Video, Now With<br />Perfectly Matched Sounds</h2>
                <p className={styles.videoToSoundDescription}>
                  Upload any video and our AI instantly generates and syncs sound effects that fit every scene—no searching, no guessing, just perfect matches automatically.
                </p>
                <ul className={styles.videoToSoundBullets}>
                  <li>Automatic time sequence</li>
                  <li>Audio editing</li>
                  <li>Export to project</li>
                </ul>
                <Link href="/editor" className={styles.videoToSoundButton}>
                  Try It Free
                  <span className={styles.buttonArrow}>→</span>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section style={{ marginTop: '8rem', paddingTop: '6rem', paddingBottom: '6rem', marginBottom: '8rem', display: 'flex', justifyContent: 'center', width: '100%' }}>
          <div style={{ maxWidth: '1200px', width: '100%', padding: '0 2rem' }}>
            {/* Section Header */}
            <div style={{ textAlign: 'center', marginBottom: '4rem', width: '100%', display: 'flex', justifyContent: 'center' }}>
              <h2 className={styles.creativeFlowTitle} style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
                Search All Your Sounds in Seconds
              </h2>
            </div>

            {/* Icons Row with Connecting Lines */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '3rem',
              margin: '0 auto 3rem',
              position: 'relative',
              width: '100%'
            }}>

              {/* Step 1: Upload Icon */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                position: 'relative'
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '20px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 32px rgba(0, 200, 255, 0.2)',
                  transition: 'all 0.3s ease'
                }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="rgba(0, 255, 255, 0.9)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <path d="M12 18v-6" />
                    <path d="m9 15 3-3 3 3" />
                  </svg>
                </div>
              </div>

              {/* Connecting Line 1 */}
              <svg width="80" height="2" viewBox="0 0 80 2" fill="none" style={{ marginTop: '0' }}>
                <line x1="0" y1="1" x2="80" y2="1" stroke="rgba(0, 255, 255, 0.3)" strokeWidth="2" strokeDasharray="4 4" />
                <polygon points="75,1 70,4 70,-2" fill="rgba(0, 255, 255, 0.3)" />
              </svg>

              {/* Step 2: Analyze Icon */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                position: 'relative'
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '20px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 32px rgba(0, 200, 255, 0.3)',
                  transition: 'all 0.3s ease'
                }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="rgba(0, 255, 255, 0.9)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                    <line x1="12" x2="12" y1="19" y2="22" />
                  </svg>
                </div>
              </div>

              {/* Connecting Line 2 */}
              <svg width="80" height="2" viewBox="0 0 80 2" fill="none" style={{ marginTop: '0' }}>
                <line x1="0" y1="1" x2="80" y2="1" stroke="rgba(0, 255, 255, 0.3)" strokeWidth="2" strokeDasharray="4 4" />
                <polygon points="75,1 70,4 70,-2" fill="rgba(0, 255, 255, 0.3)" />
              </svg>

              {/* Step 3: Export Icon */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                position: 'relative'
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '20px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 32px rgba(0, 200, 255, 0.2)',
                  transition: 'all 0.3s ease'
                }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="rgba(0, 255, 255, 0.9)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" x2="12" y1="15" y2="3" />
                  </svg>
                </div>
              </div>

            </div>

            {/* Description Text */}
            <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto 3rem', width: '100%' }}>
              <p style={{
                fontSize: '1.5rem',
                color: 'rgba(255, 255, 255, 0.8)',
                lineHeight: '1.7',
                marginBottom: '0',
                textAlign: 'center'
              }}>
                Our AI analyzes your content and instantly searches your own library plus leading high-quality sound libraries. Get perfectly matched audio that syncs automatically to your timeline and exports to any platform.
              </p>
            </div>

            {/* CTA Button */}
            <div style={{ textAlign: 'center', width: '100%', display: 'flex', justifyContent: 'center' }}>
              <Link
                href="/dashboard"
                onClick={() => trackCTAClick('start_creating', 'how_it_works')}
                style={{
                  fontWeight: 700,
                  padding: '1.25rem 3rem',
                  fontSize: '1.25rem',
                  background: 'white',
                  color: '#000',
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 4px 20px rgba(255, 255, 255, 0.15)',
                  transition: 'all 0.3s ease',
                  textDecoration: 'none',
                  display: 'inline-block',
                  cursor: 'pointer'
                }}
              >
                Start Saving
              </Link>
            </div>
          </div>
        </section>

        {/* Motion Picture Section - Four Cards Grid */}
        <section className={styles.videoToSoundSection} style={{ paddingTop: '6rem', paddingBottom: '6rem' }}>
          <div className={styles.videoToSoundContainer}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Card 1 - Turn silence into cinema */}
              <div className={styles.videoToSoundCard}>
                <div className={styles.videoToSoundMockup}>
                  <Image
                    src="/landing/Image_fx (4).jpg"
                    alt="Video to Audio Workflow"
                    fill
                    sizes="(max-width: 1024px) 100vw, 50vw"
                    style={{
                      objectFit: 'cover',
                      borderRadius: '12px'
                    }}
                    unoptimized
                  />
                </div>
                <div className={styles.videoToSoundContent}>
                  <h3 className={styles.videoToSoundTitle}>Turn silence into cinema</h3>
                  <p className={styles.videoToSoundDescription}>
                    Upload your footage and watch every frame come alive with perfectly synced sound—no hunting, no hesitation, just instant immersion.
                  </p>
                  <Link href="/editor" className={styles.videoToSoundButton}>
                    Try It Now
                    <span className={styles.buttonArrow}>→</span>
                  </Link>
                </div>
              </div>

              {/* Card 2 - Secure Project Storage */}
              <div className={styles.videoToSoundCard}>
                <div className={styles.videoToSoundMockup}>
                  <div className={styles.videoToSoundInterface}>
                    <div className={styles.videoToSoundHeader}>
                      <div className={styles.videoToSoundTitle}>Secure Project Storage</div>
                      <div className={styles.videoToSoundControls}>
                        <span className={styles.videoToSoundDot}></span>
                        <span className={styles.videoToSoundDot}></span>
                        <span className={styles.videoToSoundDot}></span>
                      </div>
                    </div>
                    <div className={styles.videoToSoundContent}>
                      <div className={styles.searchResults}>
                        <div className={styles.resultItem}>
                          <div className={styles.waveform}></div>
                          <span>Project_Cinema_Final.zenyai</span>
                        </div>
                        <div className={styles.resultItem}>
                          <div className={styles.waveform}></div>
                          <span>Assets_Backup_2024.zip</span>
                        </div>
                        <div className={styles.resultItem}>
                          <div className={styles.waveform}></div>
                          <span>Audio_Library_Secure.vault</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={styles.videoToSoundContent}>
                  <h3 className={styles.videoToSoundTitle}>Secure Asset Management</h3>
                  <p className={styles.videoToSoundDescription}>
                    Keep your creative assets and projects safe with enterprise-grade encryption and cloud storage. Automatic backups ensure your work is never lost.
                  </p>
                </div>
              </div>

              {/* Card 3 - Audio Workspace */}
              <div className={styles.videoToSoundCard}>
                <div className={styles.videoToSoundMockup}>
                  <div className={styles.videoToSoundInterface}>
                    <div className={styles.videoToSoundHeader}>
                      <div className={styles.videoToSoundTitle}>Audio Workspace</div>
                      <div className={styles.videoToSoundControls}>
                        <span className={styles.videoToSoundDot}></span>
                        <span className={styles.videoToSoundDot}></span>
                        <span className={styles.videoToSoundDot}></span>
                      </div>
                    </div>
                    <div className={styles.videoToSoundContent}>
                      <div className={styles.audioWorkspace} style={{ width: '120%' }}>
                        {/* Video icon in top left */}
                        <div className={styles.videoIcon} style={{ position: 'absolute', top: '8px', left: '8px', width: '24px', height: '24px', background: '#ff4444', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: 'white' }}>
                          ▶
                        </div>
                        {/* Vertical red timeline */}
                        <div className={styles.timeline} style={{ position: 'absolute', top: '40px', left: '20px', bottom: '8px', width: '2px', background: '#ff4444', borderRadius: '1px' }}></div>
                        {/* Audio files */}
                        <div className={styles.audioFiles}>
                          <div className={styles.soundItem}>
                            <div className={styles.soundWaveform}></div>
                            <span>Footsteps on Grass</span>
                            <div className={styles.soundTime}>0:00</div>
                          </div>
                          <div className={styles.soundItem}>
                            <div className={styles.soundWaveform}></div>
                            <span>Wind Through Trees</span>
                            <div className={styles.soundTime}>0:02</div>
                          </div>
                          <div className={styles.soundItem}>
                            <div className={styles.soundWaveform}></div>
                            <span>Bird Chirping</span>
                            <div className={styles.soundTime}>0:04</div>
                          </div>
                          <div className={styles.soundItem}>
                            <div className={styles.soundWaveform}></div>
                            <span>Distant Thunder</span>
                            <div className={styles.soundTime}>0:06</div>
                          </div>
                          <div className={styles.soundItem}>
                            <div className={styles.soundWaveform}></div>
                            <span>Sword Clash</span>
                            <div className={styles.soundTime}>0:08</div>
                          </div>
                          <div className={styles.soundItem}>
                            <div className={styles.soundWaveform}></div>
                            <span>Magic Spell</span>
                            <div className={styles.soundTime}>0:10</div>
                          </div>
                          <div className={styles.soundItem}>
                            <div className={styles.soundWaveform}></div>
                            <span>Horse Galloping</span>
                            <div className={styles.soundTime}>0:12</div>
                          </div>
                          <div className={styles.soundItem}>
                            <div className={styles.soundWaveform}></div>
                            <span>Door Creaking</span>
                            <div className={styles.soundTime}>0:14</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Audio editing title - centered on right side of split card */}
                <div style={{ position: 'absolute', top: '70%', right: '0', width: '50%', transform: 'translateY(-50%)', display: 'flex', justifyContent: 'center', alignItems: 'center', color: '#ffffff', fontSize: '20px', fontWeight: '600' }}>
                  Audio Editing & Trimming For Creative Control
                </div>
              </div>

              {/* Card 4 - Rediscover the magic */}
              <div className={styles.videoToSoundCard}>
                <div className={styles.videoToSoundMockup}>
                  <Image
                    src="/landing/Image_fx (6).jpg"
                    alt="Cinematic Storytelling"
                    fill
                    sizes="(max-width: 1024px) 100vw, 50vw"
                    style={{
                      objectFit: 'cover',
                      borderRadius: '12px'
                    }}
                    unoptimized
                  />
                </div>
                <div className={styles.videoToSoundContent}>
                  <h3 className={styles.videoToSoundTitle}>Rediscover the magic of storytelling</h3>
                  <p className={styles.videoToSoundDescription}>
                    Like the golden age of cinema, when every sound mattered and silence spoke volumes. Bring back that timeless artistry where audio and visuals danced together in perfect harmony.
                  </p>
                  <Link href="/dashboard" className={styles.videoToSoundButton}>
                    Tell Your Story
                    <span className={styles.buttonArrow}>→</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Cards Section */}
        <section className={styles.videoToSoundSection} style={{ paddingTop: '0rem', paddingBottom: '6rem' }}>
          <div className={styles.videoToSoundContainer}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Card 1 - 85% */}
              <div className={styles.videoToSoundCard} style={{ minHeight: '300px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '2rem' }}>
                <div style={{ fontSize: '6rem', fontWeight: '300', color: '#ffffff', marginBottom: '1rem' }}>85%</div>
                <p style={{ fontSize: '1.4rem', color: '#ffffff', margin: 0 }}>Faster audio production workflow</p>
              </div>

              {/* Card 2 - $50K */}
              <div className={styles.videoToSoundCard} style={{ minHeight: '300px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '2rem' }}>
                <div style={{ fontSize: '6rem', fontWeight: '300', color: '#ffffff', marginBottom: '1rem' }}>$50K</div>
                <p style={{ fontSize: '1.4rem', color: '#ffffff', margin: 0 }}>Average savings on audio production costs</p>
              </div>

              {/* Card 3 - 10x */}
              <div className={styles.videoToSoundCard} style={{ minHeight: '300px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '2rem' }}>
                <div style={{ fontSize: '6rem', fontWeight: '300', color: '#ffffff', marginBottom: '1rem' }}>10x</div>
                <p style={{ fontSize: '1.4rem', color: '#ffffff', margin: 0 }}>Faster project delivery and iteration speed</p>
              </div>
            </div>
          </div>
        </section>

        {/* Professional Capabilities Grid - Inspired by Reference Design */}
        <section className={styles.professionalGrid} style={{ marginTop: '8rem', paddingTop: '8rem', paddingBottom: '4rem', marginBottom: '0' }}>

          {/* Intelligent Audio Search Section - Full Width Two Column Layout */}
          <div style={{ width: '100%', display: 'flex', alignItems: 'center', minHeight: '80vh', marginBottom: '10rem', position: 'relative', maxWidth: '1400px', margin: '0 auto', padding: '0 2rem' }}>
            {/* Left Column - Text */}
            <div style={{ width: '50%', padding: '4rem 4rem 4rem 8rem', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <h2 className={styles.creativeFlowTitle} style={{ marginBottom: '1rem' }}>
                Never Lose Track <br /> of Your Audio Again
              </h2>
              <p style={{ fontSize: '1.4rem', color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.6 }}>
                Intelligently store, search, and manage your entire audio library with metadata-powered search. Access your personal collection alongside millions of professional audio files to keep your projects perfectly consistent.
              </p>
            </div>

            {/* Right Column - Card */}
            <div style={{ width: '50%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <div className={styles.capabilityCard} style={{ minHeight: '500px', width: '100%', maxWidth: '600px' }}>
                <div className={styles.cardMockup}>
                  <div className={styles.mockupInterface}>
                    <div className={styles.mockupHeader}>
                      <div className={styles.mockupTitle}>Smart Search Interface</div>
                      <div className={styles.mockupControls}>
                        <span className={styles.mockupDot}></span>
                        <span className={styles.mockupDot}></span>
                        <span className={styles.mockupDot}></span>
                      </div>
                    </div>
                    <div className={styles.mockupContent}>
                      {/* AI Search Animation: Database -> AI -> Folders */}
                      <div style={{ position: 'relative', height: 220, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px' }}>

                        {/* Database Icon (Left) - 3D Glass Effect */}
                        <div style={{
                          position: 'relative',
                          width: '70px',
                          height: '70px',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          borderRadius: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 20px 40px rgba(102, 126, 234, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          transform: 'perspective(1000px) rotateX(10deg) rotateY(-10deg)',
                          animation: 'float 3s ease-in-out infinite'
                        }}>
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '32px', height: '32px' }}>
                            <ellipse cx="12" cy="5" rx="9" ry="3" fill="rgba(255, 255, 255, 0.9)" />
                            <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" fill="none" stroke="rgba(255, 255, 255, 0.9)" strokeWidth="2" />
                            <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" fill="none" stroke="rgba(255, 255, 255, 0.9)" strokeWidth="2" />
                          </svg>
                          <div style={{
                            position: 'absolute',
                            top: '8px',
                            left: '8px',
                            width: '20px',
                            height: '20px',
                            background: 'rgba(255, 255, 255, 0.3)',
                            borderRadius: '50%',
                            filter: 'blur(8px)'
                          }}></div>
                        </div>

                        {/* Animated Arrow 1 - Glass Effect */}
                        <div style={{
                          width: '50px',
                          height: '50px',
                          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
                          borderRadius: '15px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                          animation: 'pulse 2s ease-in-out infinite'
                        }}>
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '24px', height: '24px' }}>
                            <path d="M5 12h14m-7-7l7 7-7 7" stroke="rgba(102, 126, 234, 0.8)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>

                        {/* AI Brain Icon (Center) - Vibrant 3D Glass */}
                        <div style={{
                          position: 'relative',
                          width: '80px',
                          height: '80px',
                          background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 50%, #feca57 100%)',
                          borderRadius: '25px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 25px 50px rgba(255, 107, 107, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
                          backdropFilter: 'blur(15px)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          transform: 'perspective(1000px) rotateX(5deg) rotateY(5deg)',
                          animation: 'glow 2s ease-in-out infinite alternate'
                        }}>
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '36px', height: '36px' }}>
                            <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" fill="rgba(255, 255, 255, 0.95)" />
                            <circle cx="12" cy="12" r="3" fill="none" stroke="rgba(255, 255, 255, 0.8)" strokeWidth="1.5" />
                            <path d="M12 1v6m0 10v6m11-7h-6m-10 0H1" stroke="rgba(255, 255, 255, 0.6)" strokeWidth="1" opacity="0.8" />
                          </svg>
                          <div style={{
                            position: 'absolute',
                            top: '10px',
                            left: '10px',
                            width: '25px',
                            height: '25px',
                            background: 'rgba(255, 255, 255, 0.4)',
                            borderRadius: '50%',
                            filter: 'blur(10px)'
                          }}></div>
                        </div>

                        {/* Animated Arrow 2 - Glass Effect */}
                        <div style={{
                          width: '50px',
                          height: '50px',
                          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
                          borderRadius: '15px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                          animation: 'pulse 2s ease-in-out infinite 0.5s'
                        }}>
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '24px', height: '24px' }}>
                            <path d="M5 12h14m-7-7l7 7-7 7" stroke="rgba(102, 126, 234, 0.8)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>

                        {/* Storage Folders (Right) - Colorful 3D Stack */}
                        <div style={{ position: 'relative', width: '70px', height: '70px' }}>
                          {/* Back Folder - Blue */}
                          <div style={{
                            position: 'absolute',
                            top: '15px',
                            left: '15px',
                            width: '55px',
                            height: '45px',
                            background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                            borderRadius: '12px',
                            boxShadow: '0 15px 30px rgba(116, 185, 255, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            transform: 'perspective(1000px) rotateX(15deg) rotateY(10deg)',
                            zIndex: 1
                          }}>
                            <div style={{
                              position: 'absolute',
                              top: '6px',
                              left: '6px',
                              width: '15px',
                              height: '15px',
                              background: 'rgba(255, 255, 255, 0.3)',
                              borderRadius: '50%',
                              filter: 'blur(6px)'
                            }}></div>
                          </div>

                          {/* Middle Folder - Green */}
                          <div style={{
                            position: 'absolute',
                            top: '8px',
                            left: '8px',
                            width: '55px',
                            height: '45px',
                            background: 'linear-gradient(135deg, #00b894 0%, #00a085 100%)',
                            borderRadius: '12px',
                            boxShadow: '0 18px 35px rgba(0, 184, 148, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            transform: 'perspective(1000px) rotateX(10deg) rotateY(5deg)',
                            zIndex: 2
                          }}>
                            <div style={{
                              position: 'absolute',
                              top: '6px',
                              left: '6px',
                              width: '15px',
                              height: '15px',
                              background: 'rgba(255, 255, 255, 0.3)',
                              borderRadius: '50%',
                              filter: 'blur(6px)'
                            }}></div>
                          </div>

                          {/* Front Folder - Pink/Purple */}
                          <div style={{
                            position: 'absolute',
                            top: '0px',
                            left: '0px',
                            width: '55px',
                            height: '45px',
                            background: 'linear-gradient(135deg, #fd79a8 0%, #e84393 100%)',
                            borderRadius: '12px',
                            boxShadow: '0 20px 40px rgba(253, 121, 168, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            transform: 'perspective(1000px) rotateX(5deg) rotateY(-5deg)',
                            zIndex: 3,
                            animation: 'bounce 3s ease-in-out infinite'
                          }}>
                            <div style={{
                              position: 'absolute',
                              top: '6px',
                              left: '6px',
                              width: '15px',
                              height: '15px',
                              background: 'rgba(255, 255, 255, 0.4)',
                              borderRadius: '50%',
                              filter: 'blur(6px)'
                            }}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={styles.cardContent}>
                  <h3 className={styles.capabilityTitle}>Intelligent Audio Search</h3>

                  {/* Logo and Icons Section */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', marginTop: '2rem', padding: '1rem' }}>

                    {/* Freesound Logo */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{
                        width: '60px',
                        height: '60px',
                        background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 8px 24px rgba(255, 107, 53, 0.3)'
                      }}>
                        <span style={{ color: 'white', fontWeight: 'bold', fontSize: '14px' }}>FS</span>
                      </div>
                      <span style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }}>Freesound</span>
                    </div>

                    {/* User Folder Icon */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{
                        width: '60px',
                        height: '60px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)'
                      }}>
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '28px', height: '28px' }}>
                          <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-9l-2-2H5a2 2 0 0 0-2 2z" fill="rgba(255, 255, 255, 0.9)" />
                        </svg>
                      </div>
                      <span style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }}>Your Files</span>
                    </div>

                    {/* Google Drive Icon */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{
                        width: '60px',
                        height: '60px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 8px 24px rgba(66, 133, 244, 0.3)'
                      }}>
                        <img
                          src="/landing/Google_Drive_icon_(2020).svg.png"
                          alt="Google Drive"
                          style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                        />
                      </div>
                      <span style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }}>Google Drive</span>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Custom Audio Generation Section - Full Width Two Column Layout */}
          <div style={{ width: '100%', display: 'flex', alignItems: 'center', minHeight: '80vh', marginBottom: '10rem', position: 'relative', maxWidth: '1400px', margin: '0 auto', padding: '0 2rem' }}>
            {/* Left Column - Card */}
            <div style={{ width: '50%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
              <div className={styles.capabilityCard} style={{ minHeight: '500px', width: '100%', maxWidth: '600px' }}>
                <div className={styles.cardMockup}>
                  <div className={styles.mockupInterface}>
                    <div className={styles.mockupHeader}>
                      <div className={styles.mockupTitle}>AI Generation Studio</div>
                      <div className={styles.mockupControls}>
                        <span className={styles.mockupDot}></span>
                        <span className={styles.mockupDot}></span>
                        <span className={styles.mockupDot}></span>
                      </div>
                    </div>
                    <div className={styles.mockupContent}>
                      {/* Audio Generation Interface with 3D Glass Effects */}
                      <div style={{ position: 'relative', height: 220, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px' }}>

                        {/* Input Prompt (Left) - 3D Glass Effect */}
                        <div style={{
                          position: 'relative',
                          width: '140px',
                          height: '80px',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          borderRadius: '20px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 20px 40px rgba(102, 126, 234, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          transform: 'perspective(1000px) rotateX(5deg) rotateY(-5deg)',
                          animation: 'float 3s ease-in-out infinite'
                        }}>
                          <div style={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.9)', textAlign: 'center', lineHeight: 1.2, padding: '8px' }}>
                            "Epic orchestral battle music"
                          </div>
                          <div style={{ fontSize: '8px', color: 'rgba(255, 255, 255, 0.7)', marginTop: '4px' }}>
                            Voice: Marcus
                          </div>
                          <div style={{
                            position: 'absolute',
                            top: '8px',
                            left: '8px',
                            width: '20px',
                            height: '20px',
                            background: 'rgba(255, 255, 255, 0.3)',
                            borderRadius: '50%',
                            filter: 'blur(8px)'
                          }}></div>
                        </div>

                        {/* Generation Arrow - Glass Effect */}
                        <div style={{
                          width: '50px',
                          height: '50px',
                          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
                          borderRadius: '15px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                          animation: 'pulse 1.5s ease-in-out infinite'
                        }}>
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '24px', height: '24px' }}>
                            <path d="M5 12h14m-7-7l7 7-7 7" stroke="rgba(255, 107, 107, 0.8)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>

                        {/* AI Generation Visualization (Center) - Pixelated Grid */}
                        <div style={{
                          position: 'relative',
                          width: '90px',
                          height: '90px',
                          background: 'linear-gradient(135deg, #ff4757 0%, #c44569 50%, #ff3838 100%)',
                          borderRadius: '25px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 25px 50px rgba(255, 71, 87, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
                          backdropFilter: 'blur(15px)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          transform: 'perspective(1000px) rotateX(5deg) rotateY(5deg)',
                          animation: 'glow 2s ease-in-out infinite alternate'
                        }}>
                          {/* Pixelated Generation Grid */}
                          <div style={{
                            position: 'relative',
                            width: '60px',
                            height: '60px'
                          }}>
                            <style>
                              {`
                              @keyframes pixelFlicker {
                                0% { opacity: 1; transform: scale(1); }
                                25% { opacity: 0.3; transform: scale(0.8); }
                                50% { opacity: 0; transform: scale(0.5); }
                                75% { opacity: 0.7; transform: scale(1.1); }
                                100% { opacity: 1; transform: scale(1); }
                              }
                            `}
                            </style>
                            {Array.from({ length: 36 }, (_, i) => {
                              const row = Math.floor(i / 6);
                              const col = i % 6;
                              const randomDelay = Math.random() * 3;
                              const randomDuration = 0.8 + Math.random() * 1.2;
                              const randomColor = Math.floor(Math.random() * 360);

                              return (
                                <div
                                  key={i}
                                  style={{
                                    position: 'absolute',
                                    left: `${col * 10}px`,
                                    top: `${row * 10}px`,
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '2px',
                                    background: `hsl(${randomColor}, 70%, 60%)`,
                                    animation: `pixelFlicker ${randomDuration}s ease-in-out infinite ${randomDelay}s`,
                                    opacity: 1
                                  }}
                                />
                              );
                            })}
                          </div>
                          <div style={{
                            position: 'absolute',
                            top: '10px',
                            left: '10px',
                            width: '25px',
                            height: '25px',
                            background: 'rgba(255, 255, 255, 0.4)',
                            borderRadius: '50%',
                            filter: 'blur(10px)'
                          }}></div>
                        </div>

                        {/* Output Arrow - Glass Effect */}
                        <div style={{
                          width: '50px',
                          height: '50px',
                          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
                          borderRadius: '15px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                          animation: 'pulse 1.5s ease-in-out infinite 0.5s'
                        }}>
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '24px', height: '24px' }}>
                            <path d="M5 12h14m-7-7l7 7-7 7" stroke="rgba(0, 184, 148, 0.8)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>

                        {/* Audio Waveform Output (Right) - 3D Glass */}
                        <div style={{
                          position: 'relative',
                          width: '120px',
                          height: '80px',
                          background: 'linear-gradient(135deg, #00b894 0%, #00a085 100%)',
                          borderRadius: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 20px 40px rgba(0, 184, 148, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          transform: 'perspective(1000px) rotateX(5deg) rotateY(5deg)'
                        }}>
                          {/* Animated Waveform */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '2px',
                            height: '40px'
                          }}>
                            {Array.from({ length: 12 }, (_, i) => (
                              <div
                                key={i}
                                style={{
                                  width: '3px',
                                  height: `${10 + Math.sin((Date.now() / 200 + i) * 0.5) * 15}px`,
                                  background: 'rgba(255, 255, 255, 0.9)',
                                  borderRadius: '2px',
                                  animation: `waveform ${0.8 + (i % 4) * 0.2}s ease-in-out infinite ${i * 0.1}s`
                                }}
                              />
                            ))}
                          </div>
                          <div style={{
                            position: 'absolute',
                            top: '8px',
                            left: '8px',
                            width: '20px',
                            height: '20px',
                            background: 'rgba(255, 255, 255, 0.3)',
                            borderRadius: '50%',
                            filter: 'blur(8px)'
                          }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={styles.cardContent}>
                  <h3 className={styles.capabilityTitle}>Custom Audio Generation</h3>

                  {/* Logo and Additional Cards Section */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', marginTop: '2rem', padding: '1rem' }}>

                    {/* Logo */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{
                        width: '80px',
                        height: '60px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)'
                      }}>
                        <img
                          src="/landing/logo-dark-mode.png"
                          alt="Logo"
                          style={{ width: '60px', height: '40px', objectFit: 'contain' }}
                        />
                      </div>
                      <span style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }}>Hume</span>
                    </div>

                    {/* Card 1 - Audio Generation */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{
                        width: '60px',
                        height: '60px',
                        background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 8px 24px rgba(255, 107, 107, 0.4)'
                      }}>
                        <img
                          src="/landing/oQhwqF72xlvtkc6uCBuCkvO4.webp"
                          alt="AI Generate"
                          style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                        />
                      </div>
                      <span style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }}>Murf</span>
                    </div>

                    {/* ElevenLabs Logo */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{
                        width: '60px',
                        height: '60px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 8px 24px rgba(255, 140, 0, 0.3)'
                      }}>
                        <img
                          src="/landing/elevenlabs58 (1).webp"
                          alt="ElevenLabs"
                          style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                        />
                      </div>
                      <span style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }}>ElevenLabs</span>
                    </div>

                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Text */}
            <div style={{ width: '50%', padding: '4rem 8rem 4rem 4rem', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <h2 className={styles.creativeFlowTitle} style={{ marginBottom: '1rem' }}>
                Create Audio That<br />Matches Your Vision
              </h2>
              <p style={{ fontSize: '1.4rem', color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.6 }}>
                Generate original music, sound effects, and professional voice-overs with AI. Choose from 155+ voices and create custom audio that perfectly fits your project's mood and style.
              </p>
            </div>
          </div>
        </section>

        {/* Chat Search Demo Section */}
        <ChatSearchDemo />

        {/* Statistics Cards Section - Full Width */}
        <section style={{ width: '100%', padding: '4rem 0', marginTop: '0', marginBottom: '4rem' }}>
          <div className={styles.videoToSoundContainer}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4 w-full">
              {/* Card 1 - Save Time */}
              <div
                style={{
                  minHeight: '300px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  padding: '2rem',
                  background: 'rgba(15, 15, 20, 0.9)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '20px',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(20, 20, 25, 0.9)';
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 25px 50px rgba(0, 0, 0, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(15, 15, 20, 0.9)';
                  e.currentTarget.style.transform = 'translateY(0px)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.4)';
                }}
              >
                <div style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <video
                    src={'/landing/Untitled%20video%20-%20Made%20with%20Clipchamp%20(7).mp4'}
                    style={{ width: '100%', height: '220px', objectFit: 'cover', borderRadius: '12px' }}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                  />
                  <CompareLineOverlay />
                </div>
              </div>

              {/* Combined Card: Message + 10x (spans two columns, same placement) */}
              <div
                className="lg:col-span-2 flex flex-col lg:flex-row items-center justify-between gap-4"
                style={{
                  minHeight: '300px',
                  padding: '2rem',
                  background: 'rgba(15, 15, 20, 0.9)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '20px',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(20, 20, 25, 0.9)';
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 25px 50px rgba(0, 0, 0, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(15, 15, 20, 0.9)';
                  e.currentTarget.style.transform = 'translateY(0px)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.4)';
                }}
              >
                <h2 style={{ fontSize: '2rem', fontWeight: 600, color: '#ffffff', lineHeight: 1.25, marginBottom: '1rem', maxWidth: '70%' }}>
                  Whether prototype sounds or final versions — achieve <span style={{ fontWeight: 800 }}>10x</span> faster delivery and iteration.
                </h2>
                <Link href="/editor" className={styles.videoToSoundButton} style={{ whiteSpace: 'nowrap', fontWeight: 700 }}>
                  Start Creating
                  <span className={styles.buttonArrow}>→</span>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Works Section — restored */}
        <Works />

        {/* Security Section - Redesigned Split Layout */}
        <section className={styles.featureCards} style={{ paddingTop: '26rem', paddingBottom: '2rem' }}>
          <div className={styles.featureCardsContainer}>
            {/* Section Title */}
            <h2 className={styles.securitySectionTitle}>
              <DecryptedText
                text="Enterprise-Grade Security & IP Protection"
                animateOn="view"
                sequential={true}
                revealDirection="start"
                speed={60}
                characters="SECURE1234567890ENCRYPT!@#$%^&*()_+ABCDEFGHIJKLMNOPQRSTUVWXYZ"
                maxIterations={20}
              />
            </h2>

            {/* Security Cards Grid */}
            <div className={styles.securityGrid}>
              {/* Left Side - Large Zero Data Retention Card */}
              <div className={styles.securityCardLarge}>
                <div className={styles.securityCardHeader}>
                  <div className={styles.securityStatusIndicator}>
                    <div className={styles.securityStatusDot}></div>
                    <h3 className={styles.securityCardTitle}>
                      <DecryptedText
                        text="Zero Data Training"
                        animateOn="view"
                        speed={50}
                        characters="ZERO123456789DATA!@#$%"
                        maxIterations={10}
                      />
                    </h3>
                  </div>
                </div>
                <div className={styles.securityCardContent}>
                  <p className={styles.securityCardDescription}>
                    Your audio files and project data are never stored permanently. All processing happens in secure, ephemeral environments with automatic cleanup after each session.
                  </p>
                  <div className={styles.securityFeatureList}>
                    <div className={styles.securityFeatureItem}>
                      <span className={styles.securityFeatureIcon}>⟲</span>
                      <span>Ephemeral Processing</span>
                    </div>
                    <div className={styles.securityFeatureItem}>
                      <span className={styles.securityFeatureIcon}>⌫</span>
                      <span>Automatic Cleanup</span>
                    </div>
                    <div className={styles.securityFeatureItem}>
                      <span className={styles.securityFeatureIcon}>◉</span>
                      <span>Session-Based Storage</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side - Stacked Cards */}
              <div className={styles.securityCardStack}>
                {/* IP-Safe Processing Card */}
                <div className={styles.securityCard}>
                  <div className={styles.securityCardHeader}>
                    <div className={styles.securityStatusIndicator}>
                      <div className={styles.securityStatusDot} style={{ background: '#8B5CF6' }}></div>
                      <h3 className={styles.securityCardTitle}>
                        <DecryptedText
                          text="IP-Safe Processing"
                          animateOn="view"
                          speed={50}
                          characters="IPSAFE987654321LOCK&*^%$#@!"
                          maxIterations={10}
                        />
                      </h3>
                    </div>
                  </div>
                  <div className={styles.securityCardContent}>
                    <p className={styles.securityCardDescription}>
                      End-to-end encryption, isolated processing environments, and SOC 2 compliance ensure your creative assets remain completely confidential.
                    </p>
                    <div className={styles.securityBadges}>
                      <span className={styles.securityBadge}>
                        <DecryptedText
                          text="SOC 2 Type II"
                          animateOn="view"
                          speed={30}
                          characters="SOC2TYPE123456789!@"
                          maxIterations={6}
                        />
                      </span>
                      <span className={styles.securityBadge}>
                        <DecryptedText
                          text="256-bit Encryption"
                          animateOn="view"
                          speed={30}
                          characters="256BIT147258369ENCRYPT!@#"
                          maxIterations={6}
                        />
                      </span>
                    </div>
                  </div>
                </div>

                {/* Enterprise Controls Card */}
                <div className={styles.securityCard}>
                  <div className={styles.securityCardHeader}>
                    <div className={styles.securityStatusIndicator}>
                      <div className={styles.securityStatusDot} style={{ background: '#3B82F6' }}></div>
                      <h3 className={styles.securityCardTitle}>
                        <DecryptedText
                          text="Enterprise Controls"
                          animateOn="view"
                          speed={50}
                          characters="ENTERPRISE12340CONTROLS!@#$"
                          maxIterations={10}
                        />
                      </h3>
                    </div>
                  </div>
                  <div className={styles.securityCardContent}>
                    <p className={styles.securityCardDescription}>
                      Team permissions, audit logging, custom security policies, and on-premise deployment options for studios requiring maximum control.
                    </p>
                    <div className={styles.securityBadges}>
                      <span className={styles.securityBadge}>
                        <DecryptedText
                          text="GDPR Compliant"
                          animateOn="view"
                          speed={30}
                          characters="GDPR987654321COMP!@#"
                          maxIterations={6}
                        />
                      </span>
                      <span className={styles.securityBadge}>Audit Logging</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid Section */}
        <section className={styles.videoToSoundSection} style={{ marginTop: '6rem', paddingTop: '4rem' }}>
          <div className={styles.videoToSoundContainer}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

              {/* Feature 1: AI Sound Search */}
              <div className={styles.videoToSoundCard} style={{ minHeight: '280px', padding: '2.5rem' }}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                </div>
                <h3 style={{ fontSize: '1.75rem', fontWeight: '600', color: 'white', marginBottom: '0.75rem' }}>
                  AI-Powered Sound Search
                </h3>
                <p style={{ fontSize: '1.125rem', color: 'rgba(255, 255, 255, 0.7)', lineHeight: '1.6' }}>
                  Search your own sound database using natural language and AI-powered metadata tagging
                </p>
              </div>

              {/* Feature 2: Smart Library */}
              <div className={styles.videoToSoundCard} style={{ minHeight: '280px', padding: '2.5rem' }}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                  </svg>
                </div>
                <h3 style={{ fontSize: '1.75rem', fontWeight: '600', color: 'white', marginBottom: '0.75rem' }}>
                  Intelligent Organization
                </h3>
                <p style={{ fontSize: '1.125rem', color: 'rgba(255, 255, 255, 0.7)', lineHeight: '1.6' }}>
                  Store and manage your entire audio library with metadata-powered search and tagging
                </p>
              </div>

              {/* Feature 3: Voice Cloning */}
              <div className={styles.videoToSoundCard} style={{ minHeight: '280px', padding: '2.5rem' }}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                    <line x1="12" x2="12" y1="19" y2="22" />
                  </svg>
                </div>
                <h3 style={{ fontSize: '1.75rem', fontWeight: '600', color: 'white', marginBottom: '0.75rem' }}>
                  Voice Cloning & TTS
                </h3>
                <p style={{ fontSize: '1.125rem', color: 'rgba(255, 255, 255, 0.7)', lineHeight: '1.6' }}>
                  Clone voices and generate speech with 155+ AI voices for character dialogue and narration
                </p>
              </div>

              {/* Feature 4: Audio Generation */}
              <div className={styles.videoToSoundCard} style={{ minHeight: '280px', padding: '2.5rem' }}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 18V5l12-2v13" />
                    <circle cx="6" cy="18" r="3" />
                    <circle cx="18" cy="16" r="3" />
                  </svg>
                </div>
                <h3 style={{ fontSize: '1.75rem', fontWeight: '600', color: 'white', marginBottom: '0.75rem' }}>
                  Generate Original Music
                </h3>
                <p style={{ fontSize: '1.125rem', color: 'rgba(255, 255, 255, 0.7)', lineHeight: '1.6' }}>
                  Create custom music and sound effects tailored to your project with AI music generation
                </p>
              </div>

              {/* Feature 5: Video Analysis */}
              <div className={styles.videoToSoundCard} style={{ minHeight: '280px', padding: '2.5rem' }}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5" />
                    <rect x="2" y="6" width="14" height="12" rx="2" />
                  </svg>
                </div>
                <h3 style={{ fontSize: '1.75rem', fontWeight: '600', color: 'white', marginBottom: '0.75rem' }}>
                  Video-to-Sound Effects
                </h3>
                <p style={{ fontSize: '1.125rem', color: 'rgba(255, 255, 255, 0.7)', lineHeight: '1.6' }}>
                  Automatically analyze videos and generate perfectly synced sound effects for every scene
                </p>
              </div>

              {/* Feature 6: Brief Upload and Analysis */}
              <div className={styles.videoToSoundCard} style={{ minHeight: '280px', padding: '2.5rem' }}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" x2="12" y1="3" y2="15" />
                  </svg>
                </div>
                <h3 style={{ fontSize: '1.75rem', fontWeight: '600', color: 'white', marginBottom: '0.75rem' }}>
                  Brief Upload & Analysis
                </h3>
                <p style={{ fontSize: '1.125rem', color: 'rgba(255, 255, 255, 0.7)', lineHeight: '1.6' }}>
                  Upload your creative brief and let AI analyze your vision to find or generate perfectly matching audio
                </p>
              </div>

            </div>
          </div>
        </section>

        {/* Testimonials Section - Loved by Indie creators and Studios - COMMENTED OUT */}
        {/* <section className={styles.testimonialSection} style={{ paddingTop: '17rem' }}>
          <div className={styles.testimonialContainer}>
            <h2 className={styles.testimonialTitle}>Loved by Indie creators and Studios</h2>
                         <div className="h-[30rem] w-full flex flex-col antialiased items-center justify-center relative overflow-hidden">
              <InfiniteMovingCards
                speed="slow"
                customDuration={400}
              >
                {testimonials.map((item, idx) => (
                  <div
                    key={item.name}
                    className="relative w-[450px] max-w-full shrink-0 px-6 py-6 md:w-[550px] mx-2"
                    style={{
                      background: 'rgba(15, 15, 20, 0.9)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
                      borderRadius: '20px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(20, 20, 25, 0.9)';
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 25px 50px rgba(0, 0, 0, 0.5)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(15, 15, 20, 0.9)';
                      e.currentTarget.style.transform = 'translateY(0px)';
                      e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.4)';
                    }}
                  >
                    <blockquote>
                      <span className="relative z-20 text-xl leading-[1.6] font-normal text-white">
                        {item.quote}
                      </span>
                      <div className="relative z-20 mt-6 flex flex-row items-center">
                        <span className="flex flex-col gap-1">
                          <span className="text-xl leading-[1.6] font-normal text-white font-semibold">
                            {item.name}
                          </span>
                          <span className="text-lg leading-[1.6] font-normal text-white/80">
                            {item.title}
                          </span>
                        </span>
                  </div>
                    </blockquote>
                  </div>
                ))}
              </InfiniteMovingCards>
            </div>
          </div>
        </section> */}

        {/* FAQ - duplicated from NewPricing, placed under testimonials */}
        <section className={pricingStyles.faqSection} style={{ paddingTop: '12rem', paddingBottom: '4rem' }}>
          <div className={pricingStyles.container}>
            <div className={pricingStyles.faqHeader}>
              <span className={pricingStyles.faqBadge}>FAQ</span>
              <h2 className={pricingStyles.faqTitle} style={{ fontSize: '3.75rem', fontWeight: 300, lineHeight: 1.1 }}>
                Answering your most common questions.
              </h2>
            </div>
            <div className={pricingStyles.faqAccordion}>
              {faqQuestions.map((faq, index) => (
                <div
                  key={index}
                  className={`${pricingStyles.faqItem} ${activeQuestion === index ? pricingStyles.faqActive : ''}`}
                  onClick={() => toggleQuestion(index)}
                >
                  <div className={pricingStyles.faqQuestion}>
                    <h3>{faq.question}</h3>
                    <span className={pricingStyles.faqToggle}>
                      {activeQuestion === index ? (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6 12H18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 6V18M6 12H18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      )}
                    </span>
                  </div>
                  {activeQuestion === index && (
                    <div className={pricingStyles.faqAnswer}>
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <Footer title="Try Zenyai Now" />
      </main>
    </div>
  );
};

// Wrapper component to handle callback
const PromoNotificationWithCallback = ({ visible, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(visible);

  useEffect(() => {
    setIsVisible(visible);
  }, [visible]);

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss();
  };

  return (
    <div className={isVisible ? "" : styles.hidden}>
      <PromoNotification
        onDismiss={handleDismiss}
      />
    </div>
  );
};

export default NewLanding; 